import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner, DarkThemeToggle } from 'flowbite-react';
import { useAuth } from '../contexts/AuthContext';
import { safeReturnPath } from '../utils/postLoginRedirect';

export const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isLoading, authError } = useAuth();
  const location = useLocation();

  // ProtectedRoute sends the page the user was trying to open; keep it
  // through sign-in so deep links (e.g. alert emails) land where they point
  const from = (location.state as { from?: { pathname: string; search?: string } } | null)?.from;
  const returnTo = safeReturnPath(from ? `${from.pathname}${from.search || ''}` : null) || '/';

  // If already authenticated, go where they were headed
  if (isAuthenticated) {
    return <Navigate to={returnTo} replace />;
  }

  // If there's a domain error, redirect to access denied
  if (authError === 'domain_not_allowed') {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col transition-colors">
      {/* Header - matches app navbar */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm px-4 lg:px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img
              src="/assets/images/aiu-logo.png"
              alt="AIU Logo"
              className="h-10 w-auto"
            />
            <span className="text-xl font-semibold text-gray-900 dark:text-white">
              AIU Directory
            </span>
          </div>
          <DarkThemeToggle className="text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg p-2.5" />
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              Welcome
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Sign in to access the directory.
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Spinner size="lg" color="info" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">Signing in...</span>
            </div>
          ) : (
            <button
              onClick={() => login(returnTo)}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 text-white bg-aiu-blue hover:bg-aiu-blue/90 rounded-lg font-medium transition-colors focus:ring-4 focus:ring-aiu-blue/50"
            >
              Sign In
            </button>
          )}

          <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Need help?{' '}
            <a
              href="mailto:servicedesk@aiu3.net"
              className="text-aiu-blue dark:text-blue-400 hover:underline"
            >
              Contact Support
            </a>
          </p>
        </div>
      </main>

      {/* Footer - matches app footer */}
      <footer className="bg-gray-800 dark:bg-gray-950 p-6 text-center border-t border-gray-700 dark:border-gray-800">
        <p className="text-sm text-gray-300 dark:text-gray-400">
          &copy; {new Date().getFullYear()} Allegheny Intermediate Unit. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default LoginPage;
